import React, { useEffect, useState } from "react";
import {
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Scatter,
  Label,
  ReferenceLine,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useStyles from "./styles";
import { fetchSupplierQuadrantData } from "../../../redux/actions";

const colors = [
  "#007BFF",
  "#8E44AD",
  "#27AE60",
  "#F39C12",
  "#E91E63",
  "#795548",
  "#FFC107",
];

const quadrantLabels = [
  { x: 25, y: 90, label: "Go-Getter" },
  { x: 75, y: 90, label: "Leaders" },
  { x: 25, y: 20, label: "Beginner" },
  { x: 75, y: 20, label: "Aspirants" },
];

// Arrow overlay
const ArrowOverlay = () => (
  <svg
    width="100%"
    height="100%"
    style={{ position: "absolute", pointerEvents: "none" }}
  >
    <defs>
      <marker
        id="arrowhead"
        markerWidth="6"
        markerHeight="6"
        refX="5"
        refY="3"
        orient="auto"
      >
        <path d="M0,0 L0,6 L6,3 Z" fill="#000" />
      </marker>
    </defs>
    <line
      x1="40"
      y1="370"
      x2="40"
      y2="150"
      stroke="#000"
      strokeWidth="1.5"
      markerEnd="url(#arrowhead)"
    />
    <line
      x1="285"
      y1="580"
      x2="600"
      y2="580"
      stroke="#000"
      strokeWidth="1.5"
      markerEnd="url(#arrowhead)"
    />
  </svg>
);

// Custom tooltip to show only the supplier name
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const supplier = payload[0].payload;
    return (
      <text
        style={{
          color: supplier.color,
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        {supplier.name}
      </text>
    );
  }
  return null;
};

const MagicQuadrant = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [scatterData, setScatterData] = useState([]);

  const supplierQuadrantRaw = useSelector(
    (state) => state.dashboard?.fetchSupplierQuadrantData?.data || {}
  );

  useEffect(() => {
    dispatch(fetchSupplierQuadrantData(props.filter));
  }, [dispatch, props.filter]);

  useEffect(() => {
    if (supplierQuadrantRaw && Object.keys(supplierQuadrantRaw).length > 0) {
      const formatted = Object.entries(supplierQuadrantRaw).map(
        ([name, scores], index) => ({
          name,
          x: (scores["Vision-Related"] || 0) * 100,
          y: (scores["Execution-Related"] || 0) * 100,
          color: colors[index % colors.length],
        })
      );
      setScatterData(formatted);
    }
  }, [supplierQuadrantRaw]);

  const CustomDot = (props) => {
    const { cx, cy, fill } = props;
    return (
      <circle cx={cx} cy={cy} r={10} fill={fill} /> // <-- r sets the radius
    );
  };

  return (
    <Box className={classes.container}>
      {scatterData.length > 0 ? (
        <>
          <Typography className={classes.topHeading}>
            Magic Quadrant for Supplier Assessment
          </Typography>

          <Box sx={{ position: "relative", display: "flex" }}>
            <ArrowOverlay />
            <ScatterChart
              width={800}
              height={600}
              margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            >
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, 100]}
                tickCount={6}
                axisLine
                tickLine
                tick={({ x, y, payload }) =>
                  payload.value === 0 ? (
                    <text
                      x={x}
                      y={y + 10}
                      textAnchor="middle"
                      fill="#666"
                      fontSize={12}
                    >
                      0
                    </text>
                  ) : (
                    <text
                      x={x}
                      y={y + 10}
                      textAnchor="middle"
                      fill="#666"
                      fontSize={12}
                    >
                      {payload.value}
                    </text>
                  )
                }
              >
                <Label
                  value="Completeness of Vision"
                  offset={-25}
                  position="insideBottomLeft"
                  dx={30}
                  fill="#000"
                />
              </XAxis>

              <YAxis
                type="number"
                dataKey="y"
                domain={[0, 100]}
                tickCount={6}
                axisLine
                tickLine
                tick={({ x, y, payload }) =>
                  payload.value === 0 ? null : (
                    <text
                      x={x - 10}
                      y={y + 4}
                      textAnchor="end"
                      fill="#666"
                      fontSize={12}
                    >
                      {payload.value}
                    </text>
                  )
                }
              >
                <Label
                  value="Ability to Execute"
                  offset={0}
                  angle={-90}
                  position="insideLeft"
                  dy={220}
                  fill="#000"
                />
              </YAxis>

              <ZAxis range={[100]} />
              <Tooltip content={<CustomTooltip />} cursor={false} />

              {/* Quadrant borders */}
              <ReferenceLine x={50} stroke="#000" />
              <ReferenceLine y={50} stroke="#000" />
              <ReferenceLine x={0} stroke="#000" />
              <ReferenceLine x={100} stroke="#000" />
              <ReferenceLine y={0} stroke="#000" />
              <ReferenceLine y={100} stroke="#000" />

              {/* Scatter points */}
              {scatterData.map((supplier) => (
                <Scatter
                  key={supplier.name}
                  name={supplier.name}
                  data={[supplier]}
                  fill={supplier.color}
                  shape={<CustomDot />}
                />
              ))}
            </ScatterChart>

            {/* Quadrant Labels */}
            <Box className={classes.labelContainer}>
              {quadrantLabels.map((q, i) => (
                <Box
                  key={i}
                  className={classes.quadrantLabel}
                  style={{
                    top: `${100 - q.y}%`,
                    left: `${q.x}%`,
                    backgroundColor: "#6E1E2A",
                  }}
                >
                  {q.label}
                </Box>
              ))}
            </Box>

            {/* Legend */}
            <Box className={classes.legendContainer}>
              {scatterData.map((supplier) => (
                <Box key={supplier.name} className={classes.legendItem}>
                  <span
                    className={classes.legendColor}
                    style={{ backgroundColor: supplier.color }}
                  ></span>
                  <Typography>
                    <span style={{ color: supplier.color, fontWeight: "bold" }}>
                      {supplier.name}
                    </span>{" "}
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "normal",
                        color: supplier.color,
                      }}
                    >
                      ({supplier.x.toFixed(0)}, {supplier.y.toFixed(0)})
                    </span>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <Typography variant="h6">
          MAGIC QUADRANT DATA IS NOT AVAILABLE
        </Typography>
      )}
    </Box>
  );
};

export default MagicQuadrant;
